import React, { useState, useEffect } from "react";
import { useFetchOrdersQuery, useAcceptOrderMutation } from "../store";
import OrderSummaryCard from "../components/orders/OrderSummaryCard";
import OrderDetails from "../components/orders/OrderDetails";
import NewOrderModal from "../components/orders/NewOrderModal";
import { twMerge } from "tailwind-merge";
import classNames from "classnames";
import { OrderSummary } from "../data/interfaces";
import { RiShoppingBag3Line, RiTimeLine } from "react-icons/ri";

const Home: React.FC = () => {
  const [showNewOrders, setShowNewOrders] = useState<boolean>(true);
  const [selectedOrderId, setSelectedOrderId] = useState<string | undefined>();
  const [newOrderPopup, setNewOrderPopup] = useState<OrderSummary | null>(null);

  // Store snoozed orders as { [orderId: string]: timestamp }
  const [snoozedOrders, setSnoozedOrders] = useState<{ [key: string]: number }>(
    {}
  );

  const { data } = useFetchOrdersQuery(undefined, {
    pollingInterval: 30000, // Poll more frequently for better UX
  });

  const [acceptOrder] = useAcceptOrderMutation();

  useEffect(() => {
    if (data?.newOrders) {
      // Clean up expired snoozes (older than 10 mins)
      const now = Date.now();
      const SNOOZE_DURATION = 10 * 60 * 1000;

      const activeSnoozes = Object.entries(snoozedOrders).reduce(
        (acc, [id, timestamp]) => {
          if (now - timestamp < SNOOZE_DURATION) {
            acc[id] = timestamp;
          }
          return acc;
        },
        {} as { [key: string]: number }
      );

      if (
        Object.keys(activeSnoozes).length !== Object.keys(snoozedOrders).length
      ) {
        setSnoozedOrders(activeSnoozes);
      }

      // Find the first new order that isn't currently snoozed
      const unsnoozedOrder = data.newOrders.find(
        (order) => !activeSnoozes[order._id]
      );

      if (unsnoozedOrder) {
        setNewOrderPopup((prev) => {
          if (prev?._id === unsnoozedOrder._id) return prev;

          // Play sound only when a new popup appears
          const audio = new Audio("/assets/sounds/notification.mp3");
          audio.play().catch((e) => console.error("Error playing sound:", e));
          return unsnoozedOrder;
        });
      } else {
        setNewOrderPopup(null);
      }
    }
  }, [data?.newOrders, snoozedOrders]);

  const handleSnooze = () => {
    if (newOrderPopup) {
      setSnoozedOrders((prev) => ({
        ...prev,
        [newOrderPopup._id]: Date.now(),
      }));
      setNewOrderPopup(null);
    }
  };

  const handleAcceptFromPopup = async (orderId: string) => {
    try {
      await acceptOrder(orderId).unwrap();
      setNewOrderPopup(null);
    } catch (err) {
      console.error("Failed to accept order:", err);
    }
  };

  const renderedNewOrders = data?.newOrders.map((order: OrderSummary) => {
    return (
      <OrderSummaryCard
        key={`new-${order._id}`}
        order={order}
        selectOrder={setSelectedOrderId}
        isSelected={selectedOrderId === order._id}
      />
    );
  });

  const renderedCurrentOrders = data?.currentOrders.map(
    (order: OrderSummary) => {
      return (
        <OrderSummaryCard
          key={`curr-${order._id}`}
          order={order}
          selectOrder={setSelectedOrderId}
          isSelected={selectedOrderId === order._id}
        />
      );
    }
  );

  const tabBaseClasses =
    "flex-1 text-center py-4 cursor-pointer transition-all duration-200 font-semibold flex items-center justify-center gap-2 border-b-2";

  const newOrdersClasses = twMerge(
    classNames(tabBaseClasses, {
      "border-primary text-primary bg-red-50": showNewOrders,
      "border-transparent text-gray-500 hover:bg-gray-50": !showNewOrders,
    })
  );

  const currentOrdersClasses = twMerge(
    classNames(tabBaseClasses, {
      "border-primary text-primary bg-red-50": !showNewOrders,
      "border-transparent text-gray-500 hover:bg-gray-50": showNewOrders,
    })
  );

  return (
    <div className="w-full flex h-[calc(100vh-64px)] bg-gray-50 overflow-hidden">
      {newOrderPopup && (
        <NewOrderModal
          order={newOrderPopup}
          onAccept={handleAcceptFromPopup}
          onClose={handleSnooze}
        />
      )}

      {/* Sidebar List */}
      <div className="w-2/5 flex flex-col h-full bg-white border-r shadow-sm z-10">
        <div className="w-full flex bg-white sticky top-0 z-10 shadow-sm">
          <div
            className={newOrdersClasses}
            onClick={() => setShowNewOrders(true)}
          >
            <RiShoppingBag3Line className="text-lg" />
            <span className="text-sm">Nouvelles</span>
            <span
              className={classNames("ml-1.5 px-1.5 py-0.5 rounded-full text-[10px]", {
                "bg-primary text-white": showNewOrders,
                "bg-gray-200 text-gray-600": !showNewOrders,
              })}
            >
              {data?.newOrders?.length || 0}
            </span>
          </div>
          <div
            className={currentOrdersClasses}
            onClick={() => setShowNewOrders(false)}
          >
            <RiTimeLine className="text-lg" />
            <span className="text-sm">En cours</span>
            <span
              className={classNames("ml-1.5 px-1.5 py-0.5 rounded-full text-[10px]", {
                "bg-primary text-white": !showNewOrders,
                "bg-gray-200 text-gray-600": showNewOrders,
              })}
            >
              {data?.currentOrders?.length || 0}
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {showNewOrders ? (
            <div className="divide-y divide-gray-100">
              {renderedNewOrders}
              {(!data?.newOrders || data.newOrders.length === 0) && (
                <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                  <RiShoppingBag3Line className="text-4xl mb-2 opacity-50" />
                  <p className="text-sm">Aucune nouvelle commande</p>
                </div>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {renderedCurrentOrders}
              {(!data?.currentOrders || data.currentOrders.length === 0) && (
                <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                  <RiTimeLine className="text-4xl mb-2 opacity-50" />
                  <p className="text-sm">Aucune commande en cours</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main Content - Order Details */}
      <div className="w-3/5 h-full bg-gray-50 overflow-y-auto p-3">
        {selectedOrderId ? (
          <OrderDetails orderId={selectedOrderId} />
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-gray-400">
            <div className="bg-white p-6 rounded-full shadow-sm mb-4">
              <RiShoppingBag3Line className="text-5xl text-gray-300" />
            </div>
            <h3 className="text-lg font-semibold text-gray-600">
              Sélectionnez une commande
            </h3>
            <p className="text-sm text-gray-500 text-center max-w-[200px]">
              Cliquez sur une commande à gauche pour voir les détails
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
